import { faArrowsRotate, faCheck, faXmark } from '@fortawesome/pro-regular-svg-icons';
import { Stack } from '@mantine/core';
import capitalize from 'lodash.capitalize';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { EPackages } from '@feathr/blackbox';
import { Button, CardV2 as Card, DatePicker, Icon, List, Select, Well } from '@feathr/components';
import { useBlackboxVersion } from '@feathr/extender/hooks';
import { useAccount, useFlags, useRole } from '@feathr/report_components';

interface IProps {
  handleFactoryReset: () => void;
  handleChangePackage: () => void;
  setNewPackage: (newPackage?: EPackages) => void;
  setStartDate: (startDate?: string) => void;
  isResetting: boolean;
  newPackage?: EPackages;
  startDate?: string;
}

interface IPackageOption {
  label: string;
  value: EPackages;
}

function FeathrLiteOnboardingCard({
  handleFactoryReset,
  handleChangePackage,
  isResetting,
  newPackage,
  startDate,
  setNewPackage,
  setStartDate,
}: Readonly<IProps>): JSX.Element {
  const { t } = useTranslation();
  const { blackboxVersion } = useBlackboxVersion();
  const { showOnboarding, enforceOnboarding } = useFlags();
  const { isGlobal } = useRole();
  const account = useAccount();

  const accountPackageOptions: IPackageOption[] = [
    { label: t('Feathr Platform - Lite'), value: EPackages.FeathrPlatformLite },
    { label: t('Feathr Platform - Base'), value: EPackages.FeathrPlatformBase },
    { label: t('Feathr Platform - Starter'), value: EPackages.FeathrPlatformStarter },
    { label: t('Feathr Platform - Essential'), value: EPackages.FeathrPlatformEssential },
    { label: t('Feathr Platform - Essential Pro'), value: EPackages.FeathrPlatformEssentialPro },
    { label: t('Feathr Platform - Advanced'), value: EPackages.FeathrPlatformAdvanced },
    { label: t('Feathr Platform - Advanced Pro'), value: EPackages.FeathrPlatformAdvancedPro },
    { label: t('Feathr Platform - Enterprise'), value: EPackages.FeathrPlatformEnterprise },
    { label: t('Feathr Platform - Enterprise Pro'), value: EPackages.FeathrPlatformEnterprisePro },
    { label: t('Finch'), value: EPackages.Finch },
    { label: t('Finch Legacy'), value: EPackages.FinchLegacy },
    { label: t('Falcon'), value: EPackages.Falcon },
    { label: t('Enterprise Falcon'), value: EPackages.EnterpriseFalcon },
    { label: t('Phoenix'), value: EPackages.Phoenix },
  ];

  function handleSelectNewPackage(newPackage?: IPackageOption): void {
    setNewPackage(newPackage?.value);
  }

  function handleClearSelectedPackage(): void {
    setNewPackage(undefined);
    setStartDate(undefined);
  }

  return (
    <>
      <Card disabled={isResetting}>
        <Card.Header title={t('Feathr Platform - Lite - Onboarding')} />
        <Card.Content action={capitalize(blackboxVersion?.DB_ENV)} title={t('Environment')} />
        <Card.Content addVerticalGap={true} title={t('Flags')}>
          <Well label={t('Show Onboarding')}>
            <Icon
              color={showOnboarding ? 'green' : 'red'}
              icon={showOnboarding ? faCheck : faXmark}
            />
          </Well>
          <Well label={t('Onboarding Enforced')}>
            <Icon
              color={enforceOnboarding ? 'green' : 'red'}
              icon={enforceOnboarding ? faCheck : faXmark}
            />
          </Well>
        </Card.Content>
        <Card.Content
          addVerticalGap={true}
          description={t("Change the account's active package.")}
          title={t('Package switcher')}
        >
          <Select
            disabled={true}
            getOptionLabel={(option): string => option.label}
            getOptionValue={(option): EPackages => option.value}
            label={t('Current package')}
            name={'account-package-select'}
            placeholder={t('Select package')}
            value={accountPackageOptions.find(
              (option) => option.value === account.activePackage?.name,
            )}
          />
          <Select<IPackageOption>
            getOptionLabel={(option): string => option.label}
            getOptionValue={(option): EPackages => option.value}
            isClearable={true}
            label={t('New package')}
            name={'account-package-select'}
            onClear={handleClearSelectedPackage}
            onSelectSingle={handleSelectNewPackage}
            options={accountPackageOptions.filter(
              (option) => option.value !== account.activePackage?.name,
            )}
            placeholder={t('Select package')}
          />
          <DatePicker
            disabled={!newPackage}
            helpText={t('Effective date of the selected package. Can be retroactive.')}
            label={t('Start date')}
            onDateStrChange={setStartDate}
            placeholder={t('Select date')}
            showCalendarIcon={true}
            value={startDate}
          />
        </Card.Content>
        <Card.Actions>
          <Button
            disabled={!isGlobal || !newPackage || isResetting}
            onClick={handleChangePackage}
            prefix={<Icon icon={faArrowsRotate} />}
            tooltip={
              !isGlobal
                ? t('Global role required')
                : !newPackage
                  ? t('New package not selected')
                  : isResetting
                    ? t('Reset in progress')
                    : undefined
            }
            type={'primary'}
          >
            {t('Change package')}
          </Button>
        </Card.Actions>
      </Card>

      <Card disabled={isResetting}>
        <Card.Header
          description={t('Fully purge the account and reset as a clean slate.')}
          isCollapsible={true}
          title={t('Factory Reset')}
        />
        <Card.Content>
          <Stack>
            <Trans t={t}>
              <List hasBullets={true}>
                <List.Item>All milestones and tasks will be reset to "Not started."</List.Item>
                <List.Item>The payment method for the account will be deleted.</List.Item>
                <List.Item>
                  ALL DATA associated with the account will be deleted. Flights, Events, Campaigns,
                  Groups, etc will all be deleted.
                </List.Item>
                <List.Item>
                  Current users (excluding those with global roles) will not have access to the
                  account and must be re-invited.
                </List.Item>
              </List>
            </Trans>
          </Stack>
        </Card.Content>
        <Card.Actions>
          <Button
            disabled={!isGlobal || isResetting}
            onClick={handleFactoryReset}
            prefix={<Icon icon={faArrowsRotate} />}
            tooltip={
              !isGlobal
                ? t('Global role required')
                : isResetting
                  ? t('Reset in progress')
                  : undefined
            }
            type={'danger'}
          >
            {t('Reset')}
          </Button>
        </Card.Actions>
      </Card>
    </>
  );
}

export default observer(FeathrLiteOnboardingCard);
